import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import Footer from '../components/Footer';
import {
  getPublicClasses,
  getPublicClassSessions,
  formatClassDateRange,
  type ClassOffering,
  type ClassSession,
} from '../features/classes';
import { getPublicClassSeries, type ClassSeriesConfig } from '../api/classes';
import { getImageUrlById } from '../features/images';
import { getClassShareUrl } from '../shared/lib';
import type { RecurrenceRule } from '../shared/types';

const offerings = [
  { title: 'Youth Madrasah', icon: 'bi-mortarboard-fill', desc: 'Afternoon madrasah for youth (Gr 1–5 and beyond). Structured Islamic education in a nurturing environment.' },
  { title: 'Adult Classes', icon: 'bi-journal-text', desc: 'Evening classes for adults. Grow in faith and knowledge alongside the community.' },
  { title: 'Sunday Lectures', icon: 'bi-megaphone-fill', desc: 'Sunday lectures after Thuhr. Join us for regular learning and reflection.' },
  { title: 'Ramadaan Programs', icon: 'bi-moon-stars-fill', desc: 'Special programs during Ramadaan including Thikr nights, hifth, and community iftar.' },
];

const CAROUSEL_AUTOPLAY_MS = 4500;

function formatRecurrence(rule: RecurrenceRule, timeStart: string, timeEnd: string): string {
  const formatTime = (t: string) => {
    const [h, m] = t.split(':').map(Number);
    const date = new Date();
    date.setHours(h, m);
    return date.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' });
  };
  const time = `${formatTime(timeStart)} – ${formatTime(timeEnd)}`;

  if (rule.frequency === 'Daily') {
    const base = rule.interval === 1 ? 'Every day' : `Every ${rule.interval} days`;
    return `${base} · ${time}`;
  }
  if (rule.frequency === 'Monthly') {
    const base = rule.interval === 1 ? 'Every month' : `Every ${rule.interval} months`;
    return `${base} · ${time}`;
  }
  // Weekly
  const base = rule.interval === 1 ? 'Every week' : `Every ${rule.interval} weeks`;
  const days = rule.byWeekday?.length ? rule.byWeekday.join(', ') : 'Weekly';
  return `${base} (${days}) · ${time}`;
}

const ClassesPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [publicClasses, setPublicClasses] = useState<ClassOffering[]>([]);
  const [previewOffering, setPreviewOffering] = useState<ClassOffering | null>(null);
  const [previewSessions, setPreviewSessions] = useState<ClassSession[]>([]);
  const [previewSessionsLoading, setPreviewSessionsLoading] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [copiedClassId, setCopiedClassId] = useState<string | null>(null);
  const [classSeriesMap, setClassSeriesMap] = useState<Record<string, ClassSeriesConfig>>({});

  useEffect(() => {
    getPublicClasses().then(setPublicClasses).catch(() => setPublicClasses([]));
  }, []);

  // Fetch series info for all classes to determine recurrence
  useEffect(() => {
    if (publicClasses.length === 0) return;
    const fetchSeries = async () => {
      const seriesMap: Record<string, ClassSeriesConfig> = {};
      await Promise.all(
        publicClasses.map(async (c) => {
          try {
            const series = await getPublicClassSeries(c.id);
            if (series) {
              seriesMap[c.id] = series;
            }
          } catch {
            // ignore
          }
        })
      );
      setClassSeriesMap(seriesMap);
    };
    fetchSeries();
  }, [publicClasses]);

  const previewIdFromUrl = searchParams.get('preview');
  useEffect(() => {
    if (!previewIdFromUrl || publicClasses.length === 0) return;
    const offering = publicClasses.find((c) => c.id === previewIdFromUrl);
    if (offering) {
      setPreviewOffering(offering);
      setSearchParams({}, { replace: true });
    }
  }, [previewIdFromUrl, publicClasses, setSearchParams]);

  const previewImageIds = previewOffering?.imageIds ?? [];
  useEffect(() => {
    setCarouselIndex(0);
  }, [previewOffering?.id]);

  useEffect(() => {
    const offeringId = previewOffering?.id;
    if (!offeringId) {
      setPreviewSessions([]);
      return;
    }
    setPreviewSessionsLoading(true);
    getPublicClassSessions(offeringId)
      .then((list) => setPreviewSessions(list))
      .catch(() => setPreviewSessions([]))
      .finally(() => setPreviewSessionsLoading(false));
  }, [previewOffering?.id]);

  useEffect(() => {
    if (previewImageIds.length <= 1) return;
    const t = setInterval(() => {
      setCarouselIndex((i) => (i + 1) % previewImageIds.length);
    }, CAROUSEL_AUTOPLAY_MS);
    return () => clearInterval(t);
  }, [previewImageIds.length]);

  const copyClassPreviewLink = async (classId: string) => {
    const url = getClassShareUrl(classId);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedClassId(classId);
      setTimeout(() => setCopiedClassId((prev) => (prev === classId ? null : prev)), 1800);
      return;
    } catch {
      // fallback below
    }

    try {
      const textarea = document.createElement('textarea');
      textarea.value = url;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'absolute';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopiedClassId(classId);
      setTimeout(() => setCopiedClassId((prev) => (prev === classId ? null : prev)), 1800);
    } catch {
      // Keep silent: share action is optional.
    }
  };

  return (
    <>
      <main className="min-vh-100">
        <section className="classes-section py-5">
          <div className="container py-4 py-md-5 px-3 px-md-0">
            <div className="row justify-content-center mb-5">
              <div className="col-lg-8 text-center">
                <span className="section-eyebrow section-eyebrow--light">Classes</span>
                <h1 className="section-title section-title--light mt-2">Engaging learning opportunities</h1>
                <p className="lead text-white-50 mb-4">
                  Welcome to Bothasig Muslim Cultural Society. We are a vibrant community dedicated to celebrating our rich heritage and sharing our values. From youth madrasah to adult education and spiritual programs—there’s a place for every learner.
                </p>
              </div>
            </div>
            <div className="row g-4 mb-5">
              {offerings.map((item, index) => (
                <div key={item.title} className="col-md-6 col-lg-3">
                  <div className="class-card" style={{ animationDelay: `${index * 0.08}s` }}>
                    <div className="class-card-icon">
                      <i className={`bi ${item.icon}`}></i>
                    </div>
                    <h3 className="class-card-title">{item.title}</h3>
                    <p className="class-card-desc mb-0">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-center">
              <Link to="/contact" className="btn btn-warning btn-lg px-4 py-3 rounded-pill fw-bold text-dark">
                <i className="bi bi-pencil-square me-2"></i>Enquire or Join
              </Link>
            </div>

            {publicClasses.length > 0 && (
              <div className="mt-5 pt-5">
                <h2 className="section-title section-title--light text-center mb-4">Upcoming & current classes</h2>
                <div className="row g-4 justify-content-center">
                  {publicClasses.map((c) => (
                    <div key={c.id} className="col-12 col-sm-6 col-lg-4">
                      <div
                        className="class-offering-card"
                        role="button"
                        tabIndex={0}
                        onClick={() => setPreviewOffering(c)}
                        onKeyDown={(e) => e.key === 'Enter' && setPreviewOffering(c)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="class-offering-card__image-wrap">
                          {(c.imageIds?.length ? c.imageIds[0] : null) ? (
                            <div className="event-image-preview-wrap h-100">
                              <img src={getImageUrlById(c.imageIds[0])} alt="" />
                              <span className="event-image-preview-hint">
                                <i className="bi bi-zoom-in" /> Click to preview
                              </span>
                            </div>
                          ) : (
                            <div className="class-offering-card__image-placeholder">
                              <i className="bi bi-image" />
                            </div>
                          )}
                        </div>
                        <div className="class-offering-card__body">
                          <h3 className="class-offering-card__title">{c.title}</h3>
                          {c.subtitle && <p className="class-offering-card__subtitle mb-0">{c.subtitle}</p>}
                          {(() => {
                            const series = classSeriesMap[c.id];
                            if (series) {
                              return (
                                <p className="class-offering-card__date class-offering-card__date--recurring">
                                  <i className="bi bi-repeat" />
                                  {formatRecurrence(series.recurrence, series.timeStart, series.timeEnd)}
                                </p>
                              );
                            }
                            if (c.dateRangeStart != null || c.dateRangeEnd != null) {
                              return (
                                <p className="class-offering-card__date">
                                  <i className="bi bi-calendar3" />
                                  {formatClassDateRange(c.dateRangeStart, c.dateRangeEnd)}
                                </p>
                              );
                            }
                            return null;
                          })()}
                        </div>
                        <div className="class-offering-card__footer">
                          <span className="class-offering-card__details">
                            Details <i className="bi bi-chevron-right" aria-hidden="true" />
                          </span>
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm class-offering-card__share-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              copyClassPreviewLink(c.id);
                            }}
                            aria-label={copiedClassId === c.id ? 'Link copied' : 'Copy class link'}
                            title={copiedClassId === c.id ? 'Copied' : 'Copy class link'}
                          >
                            <i className={`bi ${copiedClassId === c.id ? 'bi-check2' : 'bi-share-fill'}`} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      </main>

      {previewOffering && (
        <div className="modal show d-block class-offering-expanded-backdrop" role="dialog" aria-modal="true">
          <div className="modal-dialog modal-dialog-centered modal-xl modal-dialog-scrollable">
            <div className="modal-content class-offering-expanded">
              <div className="class-offering-expanded__header bg-primary text-white">
                <div>
                  <h1 className="class-offering-expanded__title">{previewOffering.title}</h1>
                  {previewOffering.subtitle && <p className="class-offering-expanded__subtitle">{previewOffering.subtitle}</p>}
                  {(() => {
                    const series = classSeriesMap[previewOffering.id];
                    if (series) {
                      return (
                        <p className="class-offering-expanded__date">
                          <i className="bi bi-repeat me-1" />
                          {formatRecurrence(series.recurrence, series.timeStart, series.timeEnd)}
                        </p>
                      );
                    }
                    if (previewOffering.dateRangeStart != null || previewOffering.dateRangeEnd != null) {
                      return (
                        <p className="class-offering-expanded__date">
                          <i className="bi bi-calendar3 me-1" />
                          {formatClassDateRange(previewOffering.dateRangeStart, previewOffering.dateRangeEnd)}
                        </p>
                      );
                    }
                    return null;
                  })()}
                </div>
                <button type="button" className="btn-close btn-close-white class-offering-expanded__close" onClick={() => setPreviewOffering(null)} aria-label="Close" />
              </div>
              <div className="class-offering-expanded__body">
                {previewImageIds.length > 0 && (
                  <div className="class-offering-expanded__images">
                    <div className="class-offering-expanded__carousel">
                      <div className="class-offering-expanded__image-wrap">
                        <img src={getImageUrlById(previewImageIds[carouselIndex])} alt="" />
                      </div>
                      {previewImageIds.length > 1 && (
                        <>
                          <button
                            type="button"
                            className="class-offering-expanded__carousel-btn class-offering-expanded__carousel-btn--prev"
                            onClick={() => setCarouselIndex((i) => (i - 1 + previewImageIds.length) % previewImageIds.length)}
                            aria-label="Previous image"
                          >
                            <i className="bi bi-chevron-left" />
                          </button>
                          <button
                            type="button"
                            className="class-offering-expanded__carousel-btn class-offering-expanded__carousel-btn--next"
                            onClick={() => setCarouselIndex((i) => (i + 1) % previewImageIds.length)}
                            aria-label="Next image"
                          >
                            <i className="bi bi-chevron-right" />
                          </button>
                          <div className="class-offering-expanded__carousel-dots">
                            {previewImageIds.map((id, idx) => (
                              <button
                                key={id}
                                type="button"
                                className={`class-offering-expanded__carousel-dot ${idx === carouselIndex ? 'active' : ''}`}
                                onClick={() => setCarouselIndex(idx)}
                                aria-label={`Go to image ${idx + 1}`}
                              />
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                    {/* Next Session - Desktop (under image) */}
                    <div className="next-session-info-box next-session-info-box--desktop d-none d-md-block mt-3">
                      <h3 className="class-offering-expanded__description-title">Upcoming sessions:</h3>
                      {previewSessionsLoading ? (
                        <p className="text-muted mb-0">Loading sessions…</p>
                      ) : previewSessions.length ? (
                        <div className="next-session-info">
                          {(() => {
                            const nextSession = previewSessions[0];
                            return (
                              <div className="d-flex gap-2 align-items-start">
                                <i className="bi bi-clock text-primary" aria-hidden />
                                <div>
                                  <div className="fw-semibold">
                                    {new Date(nextSession.startAt).toLocaleDateString('en-ZA', {
                                      weekday: 'short',
                                      year: 'numeric',
                                      month: 'short',
                                      day: 'numeric',
                                    })}
                                  </div>
                                  <div className="text-muted small">
                                    {new Date(nextSession.startAt).toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })} –{' '}
                                    {new Date(nextSession.endAt).toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })}
                                  </div>
                                </div>
                              </div>
                            );
                          })()}
                          {previewSessions.length > 1 && (
                            <p className="text-muted small mt-2 mb-0">
                              <i className="bi bi-info-circle me-1" />
                              {previewSessions.length - 1} more upcoming session{previewSessions.length - 1 > 1 ? 's' : ''}
                            </p>
                          )}
                        </div>
                      ) : (
                        <p className="text-muted mb-0">No upcoming sessions scheduled.</p>
                      )}
                    </div>
                  </div>
                )}
                <div className="class-offering-expanded__description">
                  <div className="mb-4">
                    <h3 className="class-offering-expanded__description-title">Expanded Description:</h3>
                    {previewOffering.descriptionHtml ? (
                      <div className="class-offering-description" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewOffering.descriptionHtml) }} />
                    ) : (
                      <p className="text-muted mb-0">No description.</p>
                    )}
                  </div>

                  {/* Next Session - Mobile (after content) */}
                  <div className="next-session-info-box next-session-info-box--mobile d-md-none">
                    <h3 className="class-offering-expanded__description-title">Upcoming sessions:</h3>
                    {previewSessionsLoading ? (
                      <p className="text-muted mb-0">Loading sessions…</p>
                    ) : previewSessions.length ? (
                      <div className="next-session-info">
                        {(() => {
                          const nextSession = previewSessions[0];
                          return (
                            <div className="d-flex gap-2 align-items-start">
                              <i className="bi bi-clock text-primary" aria-hidden />
                              <div>
                                <div className="fw-semibold">
                                  {new Date(nextSession.startAt).toLocaleDateString('en-ZA', {
                                    weekday: 'short',
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                  })}
                                </div>
                                <div className="text-muted small">
                                  {new Date(nextSession.startAt).toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })} –{' '}
                                  {new Date(nextSession.endAt).toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })}
                                </div>
                              </div>
                            </div>
                          );
                        })()}
                        {previewSessions.length > 1 && (
                          <p className="text-muted small mt-2 mb-0">
                            <i className="bi bi-info-circle me-1" />
                            {previewSessions.length - 1} more upcoming session{previewSessions.length - 1 > 1 ? 's' : ''}
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="text-muted mb-0">No upcoming sessions scheduled.</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="class-offering-expanded__footer">
                <button type="button" className="btn btn-primary class-offering-expanded__close-btn" onClick={() => setPreviewOffering(null)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </>
  );
};

export default ClassesPage;
